import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { TranslateModule } from '@ngx-translate/core';
import { UrlReferenceService } from '../../shared/services/url-reference.service';
import { HeaderComponent } from '../../shared/header/header.component';
import { StepperComponent } from '../../shared/stepper/stepper.component';
import { FooterComponent } from '../../shared/footer/footer.component';
import { CompanyService } from '../../shared/services/company.service';
import { AuthService } from '../../shared/services/auth.service';
import { Company } from '../../models/company.model';

@Component({
  selector: 'app-get-quote-form',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    HeaderComponent,
    StepperComponent,
    FooterComponent,
    TranslateModule,
  ],
  templateUrl: './get-quote-form.component.html',
  styleUrls: ['./get-quote-form.component.scss'],
})
export class GetQuoteFormComponent implements OnInit {
  // Stepper
  activeStep: number = 1;

  // Form fields
  companyId: string = '';
  crNumber: string = '';
  policyStartDate: string = '';
  phoneNumber: string = '';
  email: string = '';
  companyNameEn: string = '';
  companyNameAr: string = '';
  numberOfEmployees: number | undefined = undefined;

  // Options and state
  crNumberOptions: Company[] = [];
  selectedCompanyName: string = '';
  minDate: string = '';
  isLoading: boolean = true;

  // Flow control flags
  fromGetQuote: string | null = null;
  fromDashboard: string | null = null;

  // User identification
  userId: string = '';
  sponsorID: any;
  entity700Number: any;

  constructor(
    private http: HttpClient,
    private router: Router,
    private toastr: ToastrService,
    private urlRef: UrlReferenceService,
    private companyService: CompanyService,
    private authService: AuthService
  ) {}

  ngOnInit(): void {
    this.initializeComponent();
    this.loadPersistedState();
  }

  private loadPersistedState(): void {
    const applicationData = this.companyService.getApplicationData();
    console.log('Persisted application data:', applicationData);
    // Restore form fields from persisted state
    if (applicationData.companyData) {
      const company = applicationData.companyData;
      this.companyId = company.id?.toString() || '';
      this.crNumber = company.crNumber || '';
      this.selectedCompanyName = company.name || '';
      this.companyNameEn = company.name || '';
      this.numberOfEmployees = company.numberOfEmployees || 0;
      this.phoneNumber = company.contactPhone || '';
      this.companyNameAr = company.companyNameArabic || '';
      this.entity700Number = company.entity700Number || '';
      this.sponsorID = company.sponsorID || '';
      this.policyStartDate = company.policyStartDate || '';
    }

    // Restore policy start date
    if (applicationData.policyStartDate) {
      this.policyStartDate = applicationData.policyStartDate;
    }

    // Restore other fields from companyData
    if (applicationData.companyData) {
      const data = applicationData.companyData;
      this.email = data.email || this.email;
      this.phoneNumber = data.phoneNumber || this.phoneNumber;
      this.companyNameEn = data.companyNameEn || this.companyNameEn;
      this.companyNameAr = data.companyNameAr || this.companyNameAr;
      this.numberOfEmployees = data.numberOfEmployees || this.numberOfEmployees;
      this.entity700Number = data.entity700Number || this.entity700Number;
      this.sponsorID = data.sponsorID || this.sponsorID;
    }

    console.log('Loaded persisted state:', applicationData);
    console.log('Restored Entity 700 Number:', this.entity700Number);
    console.log('Restored Sponsor ID:', this.sponsorID);
  }

  private initializeComponent(): void {
    // Check flow flags
    this.fromGetQuote = localStorage.getItem('from-get-quote');
    this.fromDashboard = localStorage.getItem('from-dashboard');

    console.log('from-get-quote:', this.fromGetQuote);
    console.log('from-dashboard:', this.fromDashboard);

    // Get user ID
    this.userId = this.getUserIdFromLocalStorage();

    // Set minimum date to tomorrow
    this.initializeMinDate();

    // Load companies
    this.fetchCompanies();
  }

  private initializeMinDate(): void {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    this.minDate = tomorrow.toISOString().split('T')[0];
  }

  private getUserIdFromLocalStorage(): string {
    const keycloakProfile = localStorage.getItem('keycloakUserProfile');
    if (keycloakProfile) {
      try {
        const profile = JSON.parse(keycloakProfile);
        return profile.id;
      } catch (error) {
        console.error('Error parsing Keycloak profile:', error);
        return '';
      }
    }
    return '';
  }

  fetchCompanies(): void {
    this.isLoading = true;
    this.http
      .get<Company[]>(
        `${this.urlRef.currentAuthUrl}/companies/${this.userId}/my-companies`
      )
      .subscribe({
        next: (data) => {
          this.crNumberOptions = data;
          this.isLoading = false;
          console.log('Companies loaded:', data);
        },
        error: (err) => {
          this.isLoading = false;
          console.error('Error fetching companies:', err);
          this.toastr.error('Failed to load companies', 'Error');
        },
      });
  }

  onCompanySelect(selectedId: string): void {
    const selectedCompany = this.crNumberOptions.find(
      (cr) => cr.id === Number(selectedId)
    );

    if (selectedCompany) {
      this.crNumber = selectedCompany.crNumber;
      this.companyId = selectedId;
      this.selectedCompanyName = selectedCompany.name;
      this.companyNameEn = selectedCompany.name;
      this.numberOfEmployees = selectedCompany.numberOfEmployees || 0;
      this.phoneNumber = selectedCompany.contactPhone || '';
      this.companyNameAr = selectedCompany.companyNameArabic || '';
      this.entity700Number = selectedCompany.entity700Number || '';
      this.sponsorID = selectedCompany.sponsorID || '';

      this.companyService.setCompanyName(this.selectedCompanyName);
      this.companyService.setEmployeeCount(
        selectedCompany.numberOfEmployees || 0
      );

      // Update specific fields in the application data
      if (this.entity700Number) {
        this.companyService.setEntity700Number(this.entity700Number);
      }
      if (this.sponsorID) {
        this.companyService.setSponsorID(this.sponsorID);
      }

      // Store the complete company details
      this.companyService.setCompanyData(selectedCompany);
      this.companyService.setCompanyDetails(selectedCompany);

      console.log('Selected company with full details:', selectedCompany);
      console.log('Selected CR Number:', this.crNumber);
      console.log('Company ID:', this.companyId);
      console.log('Entity 700 Number:', this.entity700Number);
      console.log('Sponsor ID:', this.sponsorID);
    }
  }

  validateInputs(): boolean {
    // Validate company selection
    if (!this.companyId || !this.crNumber) {
      this.toastr.warning('Please select a company');
      return false;
    }

    if (!this.policyStartDate || this.policyStartDate < this.minDate) {
      this.toastr.warning('Policy start date must be a future date');
      return false;
    }

    if (!this.companyNameEn) {
      this.toastr.warning('Please enter company name in English');
      return false;
    }

    if (!this.companyNameAr) {
      this.toastr.warning('Please enter company name in Arabic');
      return false;
    }

    if (!this.numberOfEmployees || this.numberOfEmployees < 1) {
      this.toastr.warning('Please enter a valid number of employees');
      return false;
    }

    if (this.fromGetQuote === 'true') {
      if (!this.phoneNumber || !/^\d{10}$/.test(this.phoneNumber)) {
        this.toastr.warning('Please enter a valid 10-digit phone number');
        return false;
      }

      if (!this.email || !this.isValidEmail(this.email)) {
        this.toastr.warning('Please enter a valid email address');
        return false;
      }
    }

    return true;
  }

  private isValidEmail(email: string): boolean {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  }

  private saveCompanyData(companyData: any): void {
    console.log('Saving company data to backend:', companyData);

    if (!this.userId) {
      console.warn('No user ID available, skipping company save');
      return;
    }

    const companyToSave: Partial<Company> = {
      id: companyData.id,
      name: companyData.name,
      companyNameArabic: companyData.companyNameArabic,
      crNumber: companyData.crNumber,
      sponsorID: companyData.sponsorID,
      contactEmail: companyData.contactEmail,
      contactPhone: companyData.contactPhone,
      phoneNumber: companyData.phoneNumber,
      numberOfEmployees: companyData.numberOfEmployees,
      entity700Number: companyData.entity700Number,
      policyStartDate: companyData.policyStartDate,
    };

    // Check if we're updating existing company or creating new one
    if (companyToSave.id) {
      this.companyService
        .updateCompany(companyToSave.id, companyToSave, this.userId)
        .subscribe({
          next: (response) => {
            console.log('Company updated successfully:', response);
            this.companyService.setCompanyDetails(response);
          },
          error: (error) => {
            console.error('Error updating company:', error);
            this.toastr.error('Failed to update company data', 'Error');
          },
        });
    } else {
      this.companyService.createCompany(companyToSave, this.userId).subscribe({
        next: (response) => {
          console.log('Company created successfully:', response);
          this.companyService.setCompanyDetails(response);
        },
        error: (error) => {
          console.error('Error creating company:', error);
          this.toastr.error('Failed to save company data', 'Error');
        },
      });
    }
  }

  onContinue(): void {
    this.companyService.setCompanyName(this.companyNameEn);
    this.companyService.setEmployeeCount(this.numberOfEmployees || 0);

    // Update entity700Number and sponsorID in the service
    if (this.entity700Number) {
      this.companyService.setEntity700Number(this.entity700Number);
    }
    if (this.sponsorID) {
      this.companyService.setSponsorID(this.sponsorID);
    }

    this.companyService.markStepCompleted(1);

    if (!this.validateInputs()) {
      return;
    }

    localStorage.setItem('savedPolicyStartDate', this.policyStartDate);
    console.log('Policy start date saved:', this.policyStartDate);

    const companyData = {
      id: parseInt(this.companyId) || undefined,
      crNumber: this.crNumber,
      policyStartDate: this.policyStartDate,
      phoneNumber: this.phoneNumber,
      contactPhone: this.phoneNumber,
      email: this.email,
      contactEmail: this.email,
      companyNameEn: this.companyNameEn,
      companyNameAr: this.companyNameAr,
      name: this.companyNameEn,
      companyNameArabic: this.companyNameAr,
      numberOfEmployees: this.numberOfEmployees,
      entity700Number: this.entity700Number,
      sponsorID: this.sponsorID,
    };

    this.companyService.setCompanyData(companyData);

    // Save company data to backend
    this.saveCompanyData(companyData);

    if (this.fromGetQuote === 'true') {
      this.authService.setUserData({
        email: this.email,
        phoneNumber: this.phoneNumber,
        isSignup: false,
      });
    }

    if (this.fromDashboard === 'true') {
      this.handleDashboardFlow();
      return;
    }

    this.handleNormalFlow();
  }

  private handleDashboardFlow(): void {
    console.log('Skipping CR verification - coming from dashboard');

    localStorage.removeItem('from-dashboard');

    const selectedCompany = this.crNumberOptions.find(
      (cr) => cr.id === Number(this.companyId)
    );

    if (selectedCompany) {
      const companyResponse = {
        ...selectedCompany,
        policyStartDate: this.policyStartDate,
        companyNameEn: this.companyNameEn,
        companyNameAr: this.companyNameAr,
        numberOfEmployees: this.numberOfEmployees,
        phoneNumber: this.phoneNumber,
        email: this.email,
        entity700Number: this.entity700Number,
        sponsorID: this.sponsorID,
        verified: true,
      };

      console.log(
        'Using selected company details for dashboard flow:',
        companyResponse
      );

      this.companyService.setCompanyDataResponse(companyResponse);
      this.companyService.setCompanyData(companyResponse);
      this.companyService.setCompanyDetails(companyResponse);
    } else {
      // Fallback to basic company data
      const companyResponse = {
        id: parseInt(this.companyId) || 0,
        crNumber: this.crNumber,
        name: this.companyNameEn,
        companyCode: 0,
        policyStartDate: this.policyStartDate,
        companyNameEn: this.companyNameEn,
        companyNameAr: this.companyNameAr,
        numberOfEmployees: this.numberOfEmployees,
        phoneNumber: this.phoneNumber,
        email: this.email,
        sponsorID: this.sponsorID,
        entity700Number: this.entity700Number,
        verified: true,
      };

      this.companyService.setCompanyDataResponse(companyResponse as any);
      this.companyService.setCompanyData(companyResponse);
      this.companyService.setCompanyDetails(companyResponse as any);
    }

    // Navigate directly to onboarding

    this.router.navigate(['/import-employees']);
  }

  private handleNormalFlow(): void {
    console.log('Verifying CR number...');

    this.companyService
      .verifyCrNumber(this.crNumber, this.companyId)
      .subscribe({
        next: (response) => {
          console.log('CR Verified & Company Saved:', response);

          // Merge the new fields with the response
          const enrichedResponse = {
            ...response,
            companyNameEn: this.companyNameEn,
            companyNameAr: this.companyNameAr,
            numberOfEmployees: this.numberOfEmployees,
            phoneNumber: this.phoneNumber,
            email: this.email,
            entity700Number: this.entity700Number,
            sponsorID: this.sponsorID,
          };

          this.companyService.setCompanyDataResponse(enrichedResponse);
          this.companyService.setCompanyData(enrichedResponse);
          this.companyService.setCompanyDetails(enrichedResponse);

          // Handle get-quote flow with OTP
          if (this.fromGetQuote === 'true') {
            this.handleGetQuoteFlow();
          } else {
            // Navigate to onboarding for normal authenticated flow
            this.router.navigate(['/import-employees']);
          }
        },
        error: (err) => {
          console.error('Error verifying CR:', err);
          this.toastr.error('Could not verify CR. Please try again.', 'Error');
        },
      });
  }

  private handleGetQuoteFlow(): void {
    console.log('Redirecting to OTP verification');

    const userData = this.authService.getUserData();
    console.log('User Data:', userData);

    const otpPayload = {
      otpSentVia: userData.email ? 'EMAIL' : 'SMS',
      email: userData.email || this.email,
      phoneNumber: userData.phoneNumber || this.phoneNumber,
    };

    this.authService.generateOtp(otpPayload).subscribe({
      next: () => {
        this.toastr.success('OTP sent successfully');
        this.router.navigate(['/verify-otp']);
      },
      error: (err) => {
        console.error('OTP generation failed', err);
        this.toastr.error('Failed to send OTP. Please try again.', 'Error');
      },
    });
  }
}
